/**
 * The words for a disclosure's codes (SR-216's R5) — survey-api's design leaves them here in as
 * many words: "labels are the front end's". survey-api sends a disclosure's sections and fields
 * by snake_case code, and its changes, origins and Salesforce objects by their stored spelling;
 * this is the one table that says each in words, and how a field's value reads.
 *
 * **A mirror, which moves with survey-api** — the `COLUMN_DEFAULTS` precedent. It mirrors
 * `DisclosureSections.All` (the sections of Salesforce's record page, in its order) and
 * `DisclosureFields.Members` (each member's code and the section it sits in), both in
 * `Veritant.Surveys.Domain/Disclosures`, and `DisclosureChangeKinds`, `DisclosureOrigins` (with
 * `DisclosureOrigins.Iapd` since SR-396, and `.Registries` — the registries a disclosure's
 * `listedBy` names) and `DisclosureSfObjects` beside them. Nothing here orders, filters or
 * groups: the sections arrive non-empty and in the page's order, and a section is drawn where
 * survey-api put it, whatever this table says it holds.
 *
 * **A field's label is Salesforce's field name, as words** (the plan's Q3, ruled (a)): the field
 * `DisclosureFields.FromSalesforce` reads for the member — `Docket_Number_AAO__c` is "Docket
 * Number (AAO)", `Judgement_Lien_Amount__c` "Judgement/Lien Amount" — which is what staff know
 * the record by; the page layout's own labels cannot be read through the gateway. Where the
 * survey object and the team object spell a member differently, they differ only by a
 * disambiguating suffix (`Charge_Type1__c` / `Charge_Type__c`, `Disposition1__c` /
 * `Disposition__c`, `Amended_Charge_Type_Text__c` / `Amended_Charge_Type__c`), and the label is
 * the words they share. Section titles are survey-api's codes as words. Handing over the
 * layout's labels later is a change to this table alone.
 *
 * **Nothing is dropped.** A section, field, kind, origin or object this table has no words for
 * is drawn as spelled, and a field it does not know is read by its JSON type. The alternative —
 * a closed union of codes on the wire types — would turn the first member survey-api adds into
 * a value this app cannot draw. Maps, not object literals, so a code spelled like an
 * `Object.prototype` member reads as unknown rather than finding a function.
 *
 * Display, not validation: nothing here judges a value.
 */

/** How a field's value reads: text as stored, a `yyyy-MM-dd` day, a dollar amount, a plain
 *  number (FINRA's category types), or a flag that is present only when true. */
export type DisclosureFieldKind = 'text' | 'date' | 'money' | 'count' | 'flag';

interface IDisclosureFieldWords {
  code: string;
  label: string;
  kind: DisclosureFieldKind;
}

/** `DisclosureSections.All`, each with its `DisclosureFields.Members`, in survey-api's order. */
const SECTIONS: readonly { code: string; title: string; fields: IDisclosureFieldWords[] }[] = [
  {
    code: 'information',
    title: 'Information',
    fields: [
      { code: 'event_date', label: 'Event Date', kind: 'date' },
      { code: 'disclosure_type', label: 'Disclosure Type', kind: 'text' },
      { code: 'resolution', label: 'Disclosure Resolution', kind: 'text' },
      { code: 'allegations', label: 'Allegations', kind: 'text' },
      { code: 'initiated_by', label: 'Initiated By', kind: 'text' },
      { code: 'resolution_detail', label: 'Resolution', kind: 'text' },
      { code: 'sanctions', label: 'Sanctions', kind: 'text' },
      { code: 'sanction_details', label: 'Sanction Details', kind: 'text' },
      { code: 'aao_docket_number', label: 'Docket Number (AAO)', kind: 'text' }
    ]
  },
  {
    code: 'criminal',
    title: 'Criminal',
    fields: [
      { code: 'charges', label: 'Charges', kind: 'text' },
      { code: 'charge_type', label: 'Charge Type', kind: 'text' },
      { code: 'charge_disposition', label: 'Charge Disposition', kind: 'text' },
      { code: 'amended_charges', label: 'Amended Charges', kind: 'text' },
      { code: 'amended_charge_type', label: 'Amended Charge Type', kind: 'text' },
      { code: 'amended_charge_disposition', label: 'Amended Charge Disposition', kind: 'text' }
    ]
  },
  {
    code: 'financial',
    title: 'Financial',
    fields: [
      { code: 'detail_type', label: 'Type', kind: 'text' },
      { code: 'disposition', label: 'Disposition', kind: 'text' }
    ]
  },
  {
    code: 'judgment_lien',
    title: 'Judgment / Lien',
    fields: [
      { code: 'judgment_lien_amount', label: 'Judgement/Lien Amount', kind: 'money' },
      { code: 'judgment_lien_type', label: 'Judgement/Lien Type', kind: 'text' }
    ]
  },
  {
    code: 'customer_dispute',
    title: 'Customer Dispute',
    fields: [
      { code: 'damage_amount_requested', label: 'Damage Amount Requested', kind: 'money' },
      { code: 'settlement_amount', label: 'Settlement Amount', kind: 'money' },
      { code: 'damages_granted', label: 'Damages Granted', kind: 'money' },
      {
        code: 'arbitration_claim_filed_detail',
        label: 'Arbitration Claim Filed Detail',
        kind: 'text'
      },
      { code: 'arbitration_docket_number', label: 'Arbitration Docket Number', kind: 'text' }
    ]
  },
  {
    code: 'employment_separation',
    title: 'Employment Separation',
    fields: [
      { code: 'firm_name', label: 'Firm Name', kind: 'text' },
      { code: 'termination_type', label: 'Termination Type', kind: 'text' }
    ]
  },
  {
    code: 'investigation',
    title: 'Investigation',
    fields: [
      { code: 'description_of_investigation', label: 'Description of Investigation', kind: 'text' }
    ]
  },
  {
    code: 'additional',
    title: 'Additional Information',
    fields: [
      { code: 'broker_comment', label: 'Broker Comment', kind: 'text' },
      { code: 'bc_category_type', label: 'BC Category Type', kind: 'count' },
      { code: 'ia_category_type', label: 'IA Category Type', kind: 'count' },
      { code: 'is_bc_excluded', label: 'Is BC Excluded', kind: 'flag' },
      { code: 'is_iapd_excluded', label: 'Is IAPD Excluded', kind: 'flag' },
      { code: 'display_aao_link', label: 'Display AAO Link If It Exists', kind: 'flag' }
    ]
  }
];

const SECTION_TITLES: ReadonlyMap<string, string> = new Map(
  SECTIONS.map((section) => [section.code, section.title])
);

const FIELDS: ReadonlyMap<string, IDisclosureFieldWords> = new Map(
  SECTIONS.flatMap((section) => section.fields.map((field) => [field.code, field] as const))
);

/** Each field's section code — the section it sits in on Salesforce's record page. */
const FIELD_SECTIONS: ReadonlyMap<string, string> = new Map(
  SECTIONS.flatMap((section) => section.fields.map((field) => [field.code, section.code] as const))
);

/** A section's title, or its code where this table has none. */
export function sectionTitle(code: string): string {
  return SECTION_TITLES.get(code) ?? code;
}

/** A field's label and kind — `undefined` for a code this table does not know, which is drawn
 *  as spelled and read by its JSON type. */
export function disclosureField(code: string): IDisclosureFieldWords | undefined {
  return FIELDS.get(code);
}

/** The code of the section a field sits in — `undefined` for a code this table does not know. */
export function disclosureFieldSection(code: string): string | undefined {
  return FIELD_SECTIONS.get(code);
}

/** `DisclosureChangeKinds`, in words — what happened to the disclosure, as a reader says it. */
const CHANGE_KINDS: ReadonlyMap<string, string> = new Map([
  ['created', 'Appeared'],
  ['changed', 'Changed'],
  // Since SR-396 a disclosure leaves when no registry lists it any more, and returns when one
  // lists it again — BrokerCheck or IAPD — so neither kind names one (the owner's Q1).
  ['gone', 'No longer listed'],
  ['returned', 'Listed again']
]);

/** `DisclosureOrigins`, in words — who observed it: the migration's Salesforce, or a registry
 *  lookup's answer, BrokerCheck's or IAPD's. */
const ORIGINS: ReadonlyMap<string, string> = new Map([
  ['salesforce', 'Salesforce (migrated)'],
  ['brokercheck', 'BrokerCheck'],
  ['iapd', 'IAPD']
]);

/** `DisclosureOrigins` again, as the stamp on a CRD's last check — who checked it, said after
 *  "Checked … by". */
const CHECKED_BY: ReadonlyMap<string, string> = new Map([
  ['brokercheck', 'a BrokerCheck lookup'],
  ['iapd', 'an IAPD lookup'],
  ['salesforce', 'Salesforce’s refresh']
]);

/** `DisclosureOrigins.Registries`, as a disclosure's chip — one per registry its `listedBy`
 *  names (SR-396, the owner's Q1). */
const LISTED_BY: ReadonlyMap<string, string> = new Map([
  ['brokercheck', 'On BrokerCheck'],
  ['iapd', 'On IAPD']
]);

/** A disclosure no registry lists any more — `listedBy` empty, `live` false. */
export const NOT_LISTED_WORDS = 'No longer listed';

/** `DisclosureSfObjects`, in words — the survey's disclosure object and the team's. */
const SF_OBJECTS: ReadonlyMap<string, string> = new Map([
  ['Disclosure__c', 'Survey disclosure'],
  ['Team_Disclosure__c', 'Team disclosure']
]);

export function changeKindWords(kind: string): string {
  return CHANGE_KINDS.get(kind) ?? kind;
}

export function originWords(origin: string): string {
  return ORIGINS.get(origin) ?? origin;
}

export function checkedByWords(origin: string): string {
  return CHECKED_BY.get(origin) ?? origin;
}

/** One registry of a disclosure's `listedBy` as its chip — a code this table lacks drawn as
 *  spelled. */
export function listedByWords(registry: string): string {
  return LISTED_BY.get(registry) ?? registry;
}

export function sfObjectWords(sfObject: string): string {
  return SF_OBJECTS.get(sfObject) ?? sfObject;
}
